import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useAuth } from "./AuthContext";
import {
  fetchDatasetExport,
  type DatasetExportResponse,
  type DatasetExportStatus,
} from "@/services/datasetExportApi";
import { showToast } from "@/services/toast";

const STORAGE_VERSION = "v1";
const MAX_TRACKED_EXPORTS = 20;
const POLL_INTERVAL_MS = 2_000;
const TERMINAL_STATUSES = new Set<DatasetExportStatus>([
  "completed",
  "completed_with_gaps",
  "failed",
]);

export interface TrackedDatasetExportJob extends DatasetExportResponse {
  datasetId: string;
  datasetTitle: string;
  datasetHref: string;
  requestedVideoCount: number;
  requestedEventCount: number;
  completionNotifiedAt?: string | null;
}

export interface DatasetExportJobMetadata {
  datasetId: string;
  datasetTitle: string;
  datasetHref: string;
  requestedVideoCount: number;
  requestedEventCount: number;
}

interface DatasetExportJobsContextValue {
  jobs: TrackedDatasetExportJob[];
  registerJob: (
    response: DatasetExportResponse,
    metadata: DatasetExportJobMetadata,
  ) => void;
  dismissJob: (exportId: string) => void;
}

const DatasetExportJobsContext = createContext<DatasetExportJobsContextValue | null>(null);

function storageKey(uid: string): string {
  return `dataset-export-jobs:${STORAGE_VERSION}:${uid}`;
}

function isTerminal(status: DatasetExportStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

function loadJobs(uid: string): TrackedDatasetExportJob[] {
  try {
    const value = window.localStorage.getItem(storageKey(uid));
    if (!value) return [];
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (job): job is TrackedDatasetExportJob =>
          Boolean(job) &&
          typeof job.export_id === "string" &&
          typeof job.datasetId === "string" &&
          typeof job.status === "string",
      )
      .slice(0, MAX_TRACKED_EXPORTS);
  } catch {
    return [];
  }
}

function updatedTimestamp(job: TrackedDatasetExportJob): number {
  const value = job.updated_at || job.created_at;
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

function sortJobs(jobs: TrackedDatasetExportJob[]): TrackedDatasetExportJob[] {
  return [...jobs]
    .sort((left, right) => updatedTimestamp(right) - updatedTimestamp(left))
    .slice(0, MAX_TRACKED_EXPORTS);
}

export function DatasetExportJobsProvider({ children }: { children: ReactNode }) {
  const { user, getToken } = useAuth();
  const [jobs, setJobs] = useState<TrackedDatasetExportJob[]>([]);
  const [hydratedUid, setHydratedUid] = useState<string | null>(null);
  const pollInFlightRef = useRef(false);
  const jobsRef = useRef<TrackedDatasetExportJob[]>([]);
  const notifiedExportIdsRef = useRef(new Set<string>());

  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  useEffect(() => {
    setHydratedUid(null);
    if (!user?.uid) {
      setJobs([]);
      return;
    }
    const storedJobs = loadJobs(user.uid);
    storedJobs.forEach((job) => {
      if (job.completionNotifiedAt) notifiedExportIdsRef.current.add(job.export_id);
    });
    setJobs(storedJobs);
    setHydratedUid(user.uid);
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || hydratedUid !== user.uid) return;
    try {
      // Signed URLs are bearer credentials. Keep them in memory for the
      // current session, but persist only non-secret tracking metadata.
      const safeJobs = jobs.map((job) => ({ ...job, manifest_url: null }));
      window.localStorage.setItem(storageKey(user.uid), JSON.stringify(safeJobs));
    } catch {
      // Export tracking is a convenience; a storage quota/privacy failure must
      // not interfere with the export itself.
    }
  }, [hydratedUid, jobs, user?.uid]);

  const registerJob = useCallback(
    (response: DatasetExportResponse, metadata: DatasetExportJobMetadata) => {
      const tracked: TrackedDatasetExportJob = {
        ...response,
        ...metadata,
        completionNotifiedAt: null,
      };
      setJobs((current) =>
        sortJobs([tracked, ...current.filter((job) => job.export_id !== response.export_id)]),
      );
      showToast(
        `Export request received — ${metadata.requestedVideoCount.toLocaleString()} video${metadata.requestedVideoCount === 1 ? "" : "s"} queued.`,
        "info",
        { toastId: `dataset-export-received-${response.export_id}` },
      );
    },
    [],
  );

  const dismissJob = useCallback((exportId: string) => {
    setJobs((current) => current.filter((job) => job.export_id !== exportId));
  }, []);

  const activeExportIds = useMemo(
    () => jobs.filter((job) => !isTerminal(job.status)).map((job) => job.export_id),
    [jobs],
  );
  const activeExportKey = activeExportIds.join("|");

  useEffect(() => {
    if (!user?.uid || hydratedUid !== user.uid || !activeExportKey) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled || pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const ids = activeExportKey.split("|").filter(Boolean);
        const results = await Promise.allSettled(
          ids.map((exportId) => fetchDatasetExport(token, exportId)),
        );
        if (cancelled) return;

        const responses = new Map<string, DatasetExportResponse>();
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            responses.set(result.value.export_id, result.value);
          }
        });
        if (!responses.size) return;

        const currentJobsById = new Map(
          jobsRef.current.map((job) => [job.export_id, job]),
        );
        const completedNotifications = Array.from(responses.values())
          .filter((response) => isTerminal(response.status))
          .flatMap((response) => {
            const previous = currentJobsById.get(response.export_id);
            if (
              !previous ||
              isTerminal(previous.status) ||
              previous.completionNotifiedAt ||
              notifiedExportIdsRef.current.has(response.export_id)
            ) {
              return [];
            }
            notifiedExportIdsRef.current.add(response.export_id);
            return [{ ...previous, ...response }];
          });
        setJobs((current) =>
          sortJobs(
            current.map((job) => {
              const response = responses.get(job.export_id);
              if (!response) return job;
              const justFinished = !isTerminal(job.status) && isTerminal(response.status);
              const next: TrackedDatasetExportJob = { ...job, ...response };
              if (justFinished && !job.completionNotifiedAt) {
                next.completionNotifiedAt = new Date().toISOString();
              }
              return next;
            }),
          ),
        );

        completedNotifications.forEach((job) => {
          if (job.status === "failed") {
            showToast(
              `${job.datasetTitle} export failed${job.error ? `: ${job.error}` : "."}`,
              "error",
              {
                toastId: `dataset-export-complete-${job.export_id}`,
                actionLabel: "View exports",
                actionHref: job.datasetHref,
                autoClose: false,
              },
            );
            return;
          }
          const gapText = job.progress.failed
            ? ` (${job.progress.failed.toLocaleString()} failed)`
            : "";
          showToast(
            `${job.datasetTitle} export is ready — ${job.progress.built.toLocaleString()} session${job.progress.built === 1 ? "" : "s"}${gapText}.`,
            job.status === "completed_with_gaps" ? "warning" : "success",
            {
              toastId: `dataset-export-complete-${job.export_id}`,
              actionLabel: "View exports",
              actionHref: job.datasetHref,
              autoClose: false,
            },
          );
        });
      } finally {
        pollInFlightRef.current = false;
      }
    };

    void poll();
    const interval = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activeExportKey, getToken, hydratedUid, user?.uid]);

  const value = useMemo(
    () => ({ jobs, registerJob, dismissJob }),
    [dismissJob, jobs, registerJob],
  );

  return (
    <DatasetExportJobsContext.Provider value={value}>
      {children}
    </DatasetExportJobsContext.Provider>
  );
}

// Contexts intentionally export their matching hook from the same module.
// eslint-disable-next-line react-refresh/only-export-components
export function useDatasetExportJobs(): DatasetExportJobsContextValue {
  const context = useContext(DatasetExportJobsContext);
  if (!context) {
    throw new Error("useDatasetExportJobs must be used inside DatasetExportJobsProvider");
  }
  return context;
}
